import { motion, useMotionValue, useSpring } from "framer-motion";
import { useRef, type PointerEvent, type ReactNode } from "react";
import { useFinePointerMotion } from "@/hooks/useFinePointerMotion";

type MagneticProps = {
    children: ReactNode;
    /** Fraction of the pointer offset the element follows. */
    strength?: number;
    className?: string;
};

const SPRING = { stiffness: 220, damping: 16, mass: 0.4 };

/** Pulls its child toward the pointer while hovered, then springs back. */
export function Magnetic({ children, strength = 0.35, className }: MagneticProps) {
    const enabled = useFinePointerMotion();
    const ref = useRef<HTMLDivElement>(null);
    const x = useSpring(useMotionValue(0), SPRING);
    const y = useSpring(useMotionValue(0), SPRING);

    const handleMove = (event: PointerEvent<HTMLDivElement>) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!enabled || !rect) return;
        x.set((event.clientX - (rect.left + rect.width / 2)) * strength);
        y.set((event.clientY - (rect.top + rect.height / 2)) * strength);
    };

    const reset = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            className={`magnetic ${className ?? ""}`}
            style={enabled ? { x, y } : undefined}
            onPointerMove={handleMove}
            onPointerLeave={reset}
        >
            {children}
        </motion.div>
    );
}
